import { create } from 'zustand';
import Organization from '@/types/organizationType';
import emptyOrganization from '@/types/emptyOrganization';
import { clearStreamTokenCache } from '@/lib/streamAuth';

// Re-exported so the stores in this folder, which read the cache outside React,
// keep importing it from here. The implementation lives in lib/queryClient
// because a Server Component needs its own per-request client and must not
// pull a zustand store into a server render to get one.
import { getQueryClient } from '@/lib/queryClient';

export { getQueryClient };

interface OrganizationState {
  organization: Organization;
  setOrganization: (org: Organization) => void;
  setOrganizationByName: (orgName: string) => Promise<Organization>;
  setOrganizationByUUID: (uuid: string) => Promise<Organization>;
  setApiKey: (apiKey: string) => void;
  setGitOrgName: (gitOrgName: string) => void;
  clearOrganization: () => void;
}

// The cached stream token carries an org_uuid claim -- if the org identity
// actually changes, the old token would keep getting rejected by the backend
// for the rest of its ~10 minute cache window. Clear it whenever the org
// switches so the next stream call mints one scoped to the new org.
// Both sides are optional-chained on purpose: the store types `organization` as
// non-nullable, but `fetchOrganizationByName` resolves to null while the org does
// not exist yet (onboarding, before the workspace is created), so a null really
// does reach here. The previous plain `set({ organization: org })` tolerated that;
// reading `.OrgUUID` off it does not.
const applyOrganization = (
  set: (partial: Partial<OrganizationState>) => void,
  get: () => OrganizationState,
  org: Organization
) => {
  if (get().organization?.OrgUUID !== org?.OrgUUID) {
    clearStreamTokenCache();
  }
  set({ organization: org });
};

const useOrganizationStore = create<OrganizationState>()((set, get) => ({
  organization: emptyOrganization,
  setOrganization: (org: Organization) => {
    applyOrganization(set, get, org);
  },
  setOrganizationByName: async (orgName: string) => {
    if (orgName === '') return get().organization;

    // Get the queryClient
    const queryClient = getQueryClient();

    // First try to get the data from the cache
    const cachedData = queryClient.getQueryData<Organization>([
      'organization',
      'byName',
      orgName,
    ]);

    if (cachedData) {
      applyOrganization(set, get, cachedData);
      return cachedData;
    }

    // If not in cache, fetch it and update the React Query cache
    try {
      const data = await queryClient.fetchQuery({
        queryKey: ['organization', 'byName', orgName],
        queryFn: async () => {
          const response = await import('@/apis/fetchOrganization').then(
            (module) => module.fetchOrganizationByName(orgName)
          );
          return response;
        },
      });

      applyOrganization(set, get, data);
      return data;
    } catch (error) {
      console.error('Error fetching organization by name:', error);
      return get().organization;
    }
  },

  setOrganizationByUUID: async (uuid: string) => {
    if (uuid === '') return get().organization;

    // Get the queryClient
    const queryClient = getQueryClient();

    // First try to get the data from the cache
    const cachedData = queryClient.getQueryData<{ Org: Organization }>([
      'organization',
      'byUUID',
      uuid,
    ]);

    if (cachedData) {
      applyOrganization(set, get, cachedData.Org);
      return cachedData.Org;
    }

    // If not in cache, fetch it and update the React Query cache
    try {
      const data = await queryClient.fetchQuery({
        queryKey: ['organization', 'byUUID', uuid],
        queryFn: async () => {
          const response = await import('@/apis/fetchOrganization').then(
            (module) => module.fetchOrganizationByUUID(uuid)
          );
          return response;
        },
      });

      applyOrganization(set, get, data.Org);
      return data.Org;
    } catch (error) {
      console.error('Error fetching organization by UUID:', error);
      return get().organization;
    }
  },

  setApiKey: (apiKey: string) => {
    set({
      organization: {
        ...get().organization,
        ApiKey: apiKey,
      },
    });

    // Invalidate relevant queries
    const queryClient = getQueryClient();
    queryClient.invalidateQueries({ queryKey: ['organization'] });
  },

  setGitOrgName: (gitOrgName: string) => {
    set({
      organization: {
        ...get().organization,
        GitOrgName: gitOrgName,
      },
    });

    // Invalidate relevant queries
    const queryClient = getQueryClient();
    queryClient.invalidateQueries({ queryKey: ['organization'] });
  },

  clearOrganization: () => {
    set({ organization: emptyOrganization });
    // Clear React Query cache so next login fetches fresh org data
    const queryClient = getQueryClient();
    queryClient.removeQueries({ queryKey: ['organization'] });
  },
}));

export default useOrganizationStore;
